import { PATIENT_NARRATIVE_WIZARD_SECTIONS } from "./patientNarrativeWizardSections";

/**
 * What a section starts with when a question has never been saved. The
 * backend no longer fills in defaults, so these are applied client-side on
 * load and stored by the next save. They are the options marked
 * `recommended` in patient_narrative_questions_data.json (the old server-side
 * defaults), plus the ICH E3 minimum for qualifying events - deaths and other
 * serious adverse events - which the user can still deselect.
 */
export const DEFAULT_WIZARD_ANSWERS = {
  [PATIENT_NARRATIVE_WIZARD_SECTIONS.projectConventions]: {
    qCiomsFollowUpHandling: "latest_supersedes",
  },
  [PATIENT_NARRATIVE_WIZARD_SECTIONS.patientScope]: {
    qQualifyingEvents: ["death", "serious_adverse_event"],
  },
  [PATIENT_NARRATIVE_WIZARD_SECTIONS.sources]: {
    qSourceConflictResolution: "flag_for_review",
  },
  [PATIENT_NARRATIVE_WIZARD_SECTIONS.narrativeFormat]: {
    qNarrativeGrouping: "per_participant",
  },
};

// Saved answers win; a default only fills a key that was never saved (absent),
// not one saved as null.
export const withDefaultAnswers = (section, sectionData) => {
  const answers = { ...(sectionData || {}) };
  Object.entries(DEFAULT_WIZARD_ANSWERS[section] || {}).forEach(
    ([key, value]) => {
      if (answers[key] === undefined) answers[key] = value;
    }
  );
  return answers;
};

/**
 * The PATCH body for a section. PATCH is a full replacement, so this is the
 * whole page state, minus what the backend rejects:
 *
 * - keys left undefined (never answered) - dropped rather than sent;
 * - `other_events` unless "other" is selected and at least one text is
 *   given (an empty list, or one without "other", is a 422);
 * - `subject_identifier_confirmed` unless the user pressed Confirm (true).
 */
export const toSectionPayload = (section, answers) => {
  const payload = {};
  Object.entries(answers || {}).forEach(([key, value]) => {
    if (value !== undefined) payload[key] = value;
  });

  if (section === PATIENT_NARRATIVE_WIZARD_SECTIONS.patientScope) {
    const otherSelected = (payload.qQualifyingEvents || []).includes("other");
    const otherEvents = (payload.other_events || []).filter(
      text => typeof text === "string" && text.trim() !== ""
    );
    if (otherSelected && otherEvents.length > 0) {
      payload.other_events = otherEvents;
    } else {
      delete payload.other_events;
    }
    if (payload.subject_identifier_confirmed !== true) {
      delete payload.subject_identifier_confirmed;
    }
  }
  return payload;
};
